import { useState } from "react";
import { supabase } from "./supabase";
import stadiumImg from "./stadium.png";

export default function Login() {
  const [step, setStep] = useState("email"); // email | sent
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");

  const sendMagicLink = async () => {
    if (!email || !email.includes("@")) {
      setError("Please enter a valid email address");
      return;
    }
    setLoading(true);
    setError("");
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: window.location.origin,
      },
    });
    if (error) {
      setError(error.message);
    } else {
      setStep("sent");
    }
    setLoading(false);
  };

  return (
    <div style={s.container}>
      <div style={s.card}>

        {/* HEADER */}
        <div style={s.header}>
          <div style={s.logoRow}>
            <div style={s.logoIcon}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5z" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M2 17l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M2 12l10 5 10-5" stroke="#d4a017" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
            </div>
            <span style={s.logoText}>THE COACH ROOM</span>
          </div>
          <div>
            <h2 style={s.headerTitle}>
              {step === "email" ? "Welcome, Coach" : "Check your email"}
            </h2>
            <p style={s.headerSub}>
              {step === "email"
                ? "Sign in to manage your academy"
                : `We sent a login link to ${email}`}
            </p>
          </div>
        </div>

        {/* PROGRESS BAR */}
        <div style={s.progressBar}>
          <div style={{ ...s.progressFill, width: step === "email" ? "50%" : "100%" }} />
        </div>

        {/* FORM */}
        <div style={s.form}>
          {step === "email" ? (
            <>
              <div style={s.field}>
                <label style={s.label}>Email Address</label>
                <input
                  style={{
                    ...s.input,
                    borderColor: focused === "email" ? "#d4a017" : error ? "#e53e3e" : "#e8e0d0",
                    boxShadow: focused === "email" ? "0 0 0 3px rgba(212,160,23,0.1)" : "none",
                  }}
                  type="email"
                  placeholder="coach@gmail.com"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(""); }}
                  onFocus={() => setFocused("email")}
                  onBlur={() => setFocused("")}
                  onKeyDown={(e) => e.key === "Enter" && sendMagicLink()}
                />
              </div>

              {error && <div style={s.error}>{error}</div>}

              <button
                style={loading ? s.btnDisabled : s.btn}
                onClick={sendMagicLink}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Login Link →"}
              </button>

              <p style={s.note}>
                We'll send a secure login link to your email. No password needed.
              </p>
            </>
          ) : (
            <>
              <div style={s.sentBox}>
                <div style={s.sentIcon}>📬</div>
                <div style={s.sentTitle}>Login link sent!</div>
                <div style={s.sentDesc}>
                  Open your email and click the <strong>"Sign In"</strong> link we sent to <strong>{email}</strong>. It will log you in automatically.
                </div>
              </div>

              <div style={s.tipBox}>
                <div style={s.tipTitle}>💡 Can't find the email?</div>
                <div style={s.tipDesc}>Check your spam or junk folder. It arrives within 1 minute.</div>
              </div>

              <button style={s.backBtn} onClick={() => { setStep("email"); setError(""); }}>
                ← Try a different email
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #0f1f14 0%, #1a3a1b 50%, #0f1f14 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: "2rem 1rem", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: "border-box",
  },
  card: {
    background: "#faf7f0", width: "100%", maxWidth: "420px",
    boxShadow: "0 24px 64px rgba(0,0,0,0.4), 0 0 0 1px rgba(212,160,23,0.2)",
    overflow: "hidden", margin: "0 auto",
  },
  header: {
    background: `linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(10,20,10,0.88) 65%, rgba(250,247,240,1) 100%), url(${stadiumImg})`,
    backgroundSize: "cover", backgroundPosition: "center 30%",
    padding: "2rem 1.8rem 3rem", boxSizing: "border-box",
  },
  logoRow: { display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "2rem" },
  logoIcon: {
    width: "36px", height: "36px", background: "rgba(212,160,23,0.15)",
    border: "1px solid rgba(212,160,23,0.4)", borderRadius: "8px",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  logoText: { color: "white", fontSize: "0.75rem", fontWeight: "800", letterSpacing: "3px" },
  headerTitle: { color: "white", fontSize: "1.5rem", fontWeight: "700", margin: "0 0 0.3rem", letterSpacing: "-0.3px" },
  headerSub: { color: "rgba(255,255,255,0.55)", fontSize: "0.85rem", margin: 0, fontWeight: "300" },
  progressBar: { height: "3px", background: "#e8e0d0" },
  progressFill: { height: "100%", background: "linear-gradient(90deg, #1a3a1b, #d4a017)", transition: "width 0.4s ease" },
  form: { padding: "1.8rem 1.8rem 2rem", boxSizing: "border-box" },
  field: { marginBottom: "1.2rem" },
  label: { display: "block", fontSize: "0.7rem", fontWeight: "700", textTransform: "uppercase", letterSpacing: "1px", color: "#4a5568", marginBottom: "0.45rem" },
  input: {
    width: "100%", padding: "0.85rem 1rem", border: "1.5px solid #e8e0d0",
    fontSize: "1rem", fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    outline: "none", background: "white", color: "#1a1a1a",
    transition: "border-color 0.15s, box-shadow 0.15s", borderRadius: "0", boxSizing: "border-box",
  },
  btn: {
    width: "100%", padding: "1rem", background: "#1a3a1b", color: "white",
    border: "none", fontSize: "0.95rem", fontWeight: "700", cursor: "pointer",
    letterSpacing: "0.5px", marginBottom: "0.8rem", boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  btnDisabled: {
    width: "100%", padding: "1rem", background: "#a0aec0", color: "white",
    border: "none", fontSize: "0.95rem", fontWeight: "700", cursor: "not-allowed",
    letterSpacing: "0.5px", marginBottom: "0.8rem", boxSizing: "border-box",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  backBtn: {
    width: "100%", padding: "0.7rem", background: "transparent", color: "#6b7280",
    border: "none", fontSize: "0.82rem", fontWeight: "600", cursor: "pointer",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  error: {
    background: "#fde8de", color: "#9b1c1c", padding: "0.7rem 0.9rem",
    fontSize: "0.8rem", fontWeight: "600", marginBottom: "1rem",
    borderLeft: "3px solid #e53e3e",
  },
  note: { textAlign: "center", fontSize: "0.72rem", color: "#a0aec0", fontStyle: "italic", margin: 0, lineHeight: "1.5" },
  sentBox: {
    background: "#f0fdf4", border: "1px solid #d4f1e0", padding: "1.5rem",
    textAlign: "center", marginBottom: "1rem",
  },
  sentIcon: { fontSize: "2.5rem", marginBottom: "0.8rem" },
  sentTitle: { fontWeight: "800", fontSize: "1.1rem", color: "#1a3a1b", marginBottom: "0.5rem" },
  sentDesc: { fontSize: "0.85rem", color: "#4a5568", lineHeight: "1.6" },
  tipBox: {
    background: "#fff8e1", border: "1px solid rgba(212,160,23,0.3)",
    padding: "0.8rem 1rem", marginBottom: "1rem",
  },
  tipTitle: { fontSize: "0.78rem", fontWeight: "700", color: "#7a5c00", marginBottom: "0.2rem" },
  tipDesc: { fontSize: "0.75rem", color: "#7a5c00", fontWeight: "300" },
};